/*
 * Audit C6 - product card image gallery
 * Nexus Elite Solutions - 1st October 2026
 *
 * Replaces the desktop-only hover image with a real mini gallery inside the
 * product card: swipe on touch, arrows on click, dots at the bottom-left, and
 * next/prev wrap around (loop).
 *
 * This is a CUSTOM ELEMENT on purpose. The collection grid is re-injected with
 * innerHTML when the shopper filters or sorts, and a <script> tag inserted that
 * way never runs - but a custom element upgrades itself automatically. Same
 * pattern the theme uses for carousel-element / navigation-element.
 *
 * Scrolling is native (CSS scroll-snap), so the gallery still swipes correctly
 * even if this file fails to load.
 *
 * 10th October 2026 - IT ALSO ASKS THE CAROUSEL TO RE-MEASURE.
 *
 * The client: the product cards on the last page of the homepage carousels
 * collapsed to a thin band with no title and no price. The cause is in the
 * theme, and this gallery is what exposes it.
 *
 * carousel-element measures each block ONCE and then pins the slide:
 *
 *   getHeights()      writes --block-height from block.offsetHeight
 *   setSliderHeight() reads those back and sets --slide-height
 *
 * getHeights() is only ever reached from a ResizeObserver, so after that first
 * pass nothing re-measures unless the carousel's WIDTH changes. Swiping does
 * not re-measure.
 *
 * The client's own inspector showed exactly this on New Arrivals:
 *
 *   --block-height: 236      (blocks 1-4, measured with their picture)
 *   --media-height: 156
 *   --slide-height: 80px     (what the last slide was pinned to)
 *
 * 236 - 156 = 80. The last slide was pinned to the height of a card with no
 * picture in it: those blocks are furthest off-screen, their images are lazy,
 * and the one measurement happened before the images arrived.
 *
 * So each gallery waits for its own images and then asks the enclosing
 * carousel to measure again. resetHeight() is the theme's own public method -
 * getHeights() plus setSliderHeight(), exactly what a resize already triggers -
 * so nothing here reaches into carousel internals or recomputes a height
 * itself. The calls are batched into one frame, so a carousel with sixteen
 * images re-measures once rather than sixteen times.
 */

/* Shared across every card on the page: one re-measure per carousel per frame. */
var pendingResets = new Set();
var resetQueued = false;

function queueCarouselReset(carousel) {
  if (!carousel || typeof carousel.resetHeight !== 'function') return;

  pendingResets.add(carousel);
  if (resetQueued) return;
  resetQueued = true;

  window.requestAnimationFrame(function () {
    resetQueued = false;
    pendingResets.forEach(function (el) {
      try {
        el.resetHeight();
      } catch (error) {
        /* a carousel removed mid-flight is not worth breaking the page over */
      }
    });
    pendingResets.clear();
  });
}

class ProductCardGallery extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;

    this.track = this.querySelector('[data-pc-track]');
    if (!this.track) return;

    /* Before the early return below - a card with a single slide still has a
       picture whose arrival changes the block's height, and the carousel needs
       to hear about that just as much. */
    this.watchImagesForCarousel();

    this.slides = this.querySelectorAll('.product-card--gallery-slide');
    this.count = this.slides.length;
    if (this.count < 2) return;

    this._ready = true;
    this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-pc-dot]'));
    this._index = 0;

    this._onScroll = this._onScroll.bind(this);
    this.track.addEventListener('scroll', this._onScroll, { passive: true });

    const prev = this.querySelector('[data-pc-prev]');
    const next = this.querySelector('[data-pc-next]');

    if (prev) prev.addEventListener('click', (e) => this._nudge(e, -1));
    if (next) next.addEventListener('click', (e) => this._nudge(e, 1));

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', (e) => {
        /* the image sits inside a link to the product - don't follow it */
        e.preventDefault();
        e.stopPropagation();
        this.goTo(i);
      });
    });
  }

  watchImagesForCarousel() {
    var carousel = this.closest('carousel-element');
    if (!carousel) return;

    var images = this.querySelectorAll('img');

    for (var i = 0; i < images.length; i += 1) {
      var image = images[i];

      /* Already decoded - it was part of the measurement, nothing to wait for. */
      if (image.complete && image.naturalHeight > 0) continue;

      image.addEventListener('load', function () {
        queueCarouselReset(carousel);
      }, { once: true });

      /* A broken image settles the layout too, so re-measure on that as well
         rather than leaving the slide pinned to a height that never resolves. */
      image.addEventListener('error', function () {
        queueCarouselReset(carousel);
      }, { once: true });
    }
  }

  disconnectedCallback() {
    if (this.track && this._onScroll) {
      this.track.removeEventListener('scroll', this._onScroll);
    }
  }

  _nudge(event, step) {
    event.preventDefault();
    event.stopPropagation();
    this.goTo(this._index + step);
  }

  goTo(target) {
    /* wrap around in both directions so next on the last image loops to the first */
    const index = ((target % this.count) + this.count) % this.count;
    const width = this.track.clientWidth || 0;

    this.track.scrollTo({ left: width * index, behavior: 'smooth' });
    this._setIndex(index);
  }

  _onScroll() {
    /* debounce: fires a lot during a swipe */
    clearTimeout(this._scrollTimer);
    this._scrollTimer = setTimeout(() => {
      const width = this.track.clientWidth || 1;
      this._setIndex(Math.round(this.track.scrollLeft / width));
    }, 60);
  }

  _setIndex(index) {
    if (index < 0 || index >= this.count) return;
    this._index = index;
    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }
}

if (!customElements.get('product-card-gallery')) {
  customElements.define('product-card-gallery', ProductCardGallery);
}
